import {
  LBBadge,
  LBArrowCornerRightUpIcon,
  LBSkeleton,
  LBSeparator,
  LBScrollArea
} from '@linearb/buzz'
import type { JiraUser, JiraSelectField, JiraStatus } from '../types'
import { JIRA_BASE_URL } from '../views/FeatureRequests/FeatureRequests.consts'
import { parseUtc } from '../utils/dateHelpers'

export { JIRA_BASE_URL as JIRA_BROWSE }

export const getStatusVariant = (
  status?: JiraStatus
): 'default' | 'blue' | 'green' => {
  const key = status?.statusCategory?.key
  if (key === 'done') {
    return 'green'
  }
  if (key === 'indeterminate') {
    return 'blue'
  }
  return 'default'
}

export const RichText: React.FC<{ text: string }> = ({ text }) => {
  const isHtml = /<[a-z][\s\S]*>/i.test(text)
  if (isHtml) {
    return (
      <div
        className='[&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:mb-0.5 [&_p]:mb-1 [&_p:last-child]:mb-0 [&_table]:w-full [&_table]:text-xs [&_th]:text-left [&_th]:font-semibold [&_th]:py-1 [&_th]:pr-3 [&_td]:py-0.5 [&_td]:pr-3 [&_h1]:text-sm [&_h1]:font-bold [&_h1]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-1 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:mb-1'
        dangerouslySetInnerHTML={{ __html: text }}
      />
    )
  }
  return <span className='whitespace-pre-wrap'>{text}</span>
}

export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
    {children}
  </h3>
)

export const FieldRow: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <div className='grid grid-cols-[140px_1fr] gap-3 py-2.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <span className='text-sm text-foreground font-medium'>{children}</span>
  </div>
)

export const JiraLink: React.FC<{
  href: string
  children: React.ReactNode
}> = ({ href, children }) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors'
  >
    {children}
    <LBArrowCornerRightUpIcon className='h-3 w-3' />
  </a>
)

export const UserField: React.FC<{ label: string; user?: JiraUser }> = ({
  label,
  user
}) => (
  <div className='grid grid-cols-[140px_1fr] gap-3 py-2.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    {user?.displayName ? (
      <span className='text-sm text-foreground font-medium inline-flex items-center gap-2'>
        {user.avatarUrl && (
          <img
            src={user.avatarUrl}
            alt={user.displayName}
            className='h-5 w-5 rounded-full'
          />
        )}
        {user.displayName}
      </span>
    ) : (
      <span className='text-sm text-foreground font-medium'>-</span>
    )}
  </div>
)

export const BadgeList: React.FC<{
  label: string
  items: JiraSelectField[]
  variant: 'secondary' | 'outline'
}> = ({ label, items, variant }) => (
  <div className='grid grid-cols-[140px_1fr] gap-3 py-2.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <div className='flex flex-wrap gap-2'>
      {items.map((item, i) => (
        <LBBadge key={i} variant={variant}>
          {item.value}
        </LBBadge>
      ))}
    </div>
  </div>
)

export const ScrollableBlock: React.FC<{
  title: string
  content: string
}> = ({ title, content }) => (
  <>
    <LBSeparator />
    <div>
      <SectionTitle>{title}</SectionTitle>
      <LBScrollArea className='max-h-32'>
        <div className='text-xs text-foreground bg-muted rounded-md p-3 leading-relaxed'>
          <RichText text={content} />
        </div>
      </LBScrollArea>
    </div>
  </>
)

export const LoadingSkeleton: React.FC = () => (
  <div className='space-y-4 pt-2'>
    <div className='space-y-2'>
      <LBSkeleton className='h-5 w-2/3 rounded' />
      <LBSkeleton className='h-4 w-full rounded' />
    </div>
    <LBSeparator />
    {[...Array(3)].map((_, i) => (
      <div key={i} className='space-y-2'>
        <LBSkeleton className='h-3 w-20 rounded' />
        <LBSkeleton className='h-4 w-full rounded' />
        <LBSkeleton className='h-4 w-3/4 rounded' />
      </div>
    ))}
  </div>
)

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) {
    return '-'
  }
  const date = parseUtc(dateStr)
  if (!date.isValid()) {
    return dateStr
  }
  return date.format('MMM D, YYYY')
}
